import CheckIcon from '@mui/icons-material/Check';
import {
    Alert,
    Box,
    Button,
    FormControlLabel,
    Link,
    Skeleton,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { DeveloperApiError } from '../api';
import { ErrorAlert, mono, TableScroll, ToneChip } from '../components';
import type { Integration, TagCategoryGrid, TagCategoryGridRow } from '../integrationTypes';
import { useIntegrations } from './IntegrationsContext';
import { CardSection } from './parts';
import { FlagChips, RemoveDefaultDialog, SeedDialog, TagCategoryFlagsDialog } from './TagCategoryDialogs';
import {
    crewHint, emitHint, emitOrigin, onLabels, STATUS_ORDER, tagCategoryStatus, valuesFromDefault, valuesFromSeed,
} from './tagCategoryFlags';

/** In place of "Add all recommended" when the grid says it does not apply and gives no reason. */
const SEED_NOT_APPLICABLE_HINT = 'Adding the recommended defaults does not apply to this company.';

/** Rows without a default that Add offers (a `missing_category` row has no category to add a default for). */
const ADDABLE = new Set(['needs_default', 'inactive', 'informational']);

const rowName = (row: TagCategoryGridRow) => row.category?.name || row.name || 'Named by the booking';
const rowKey = (row: TagCategoryGridRow) => row.code ?? '_dynamic';

/** Mismatches first, then the server's order (stable). */
const sortRows = (rows: TagCategoryGridRow[]) => {
    const rank = (row: TagCategoryGridRow) => {
        const i = STATUS_ORDER.indexOf(row.status);
        return i < 0 ? STATUS_ORDER.length : i;
    };
    return rows.map((row, i) => ({ row, i })).sort((a, b) => rank(a.row) - rank(b.row) || a.i - b.i).map(({ row }) => row);
};

type Editing = { mode: 'add' | 'edit'; row: TagCategoryGridRow };

/** The words after a 409: the table is reloaded, and says why. */
const conflictNotice = (error: DeveloperApiError) => (
    error.code === 'inherited_default'
        ? `${error.body?.detail || error.message} The table shows the defaults as they are now.`
        : 'These defaults were changed elsewhere since you opened them: the table shows them as they are now. Check it and try again.'
);

const CategoryCell = ({ row }: { row: TagCategoryGridRow }) => (
    <Box sx={{ minWidth: 140 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{rowName(row)}</Typography>
        {row.code && <Typography variant="caption" color="text.secondary" component="div" sx={{ ...mono, fontSize: 12 }}>{row.code}</Typography>}
        {row.category && (
            <Link component={RouterLink} to={`/tags?category=${row.category.id}`} variant="caption" aria-label={`Tags in ${rowName(row)}`} data-testid="tags-link">Tags</Link>
        )}
        {row.missing && <Typography variant="caption" color="text.secondary" component="div">No such category</Typography>}
    </Box>
);

const DefaultCell = ({ row, flagLabels, showOwn }: { row: TagCategoryGridRow; flagLabels: Record<string, string>; showOwn: boolean }) => {
    const def = row.default;
    if (!def) {
        return <Typography variant="body2" color="text.secondary">—</Typography>;
    }
    return (
        <Stack direction="row" spacing={0.75} alignItems="flex-start">
            <CheckIcon fontSize="small" color="success" titleAccess="Has a default" sx={{ mt: 0.25 }} />
            <Box sx={{ minWidth: 0 }}>
                <FlagChips labels={onLabels(valuesFromDefault(def), flagLabels)} />
                {def.inherited_from ? (
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }} data-testid="inherited-from">
                        from {def.inherited_from.name}
                    </Typography>
                ) : showOwn && <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>own</Typography>}
            </Box>
        </Stack>
    );
};

const EmitsCell = ({ row }: { row: TagCategoryGridRow }) => (
    row.emits.length ? (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
            {row.emits.map((emit, i) => (
                <ToneChip
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${emit.origin}-${emit.account || ''}-${i}`}
                    label={emitOrigin(emit.origin)}
                    tone={emit.active ? 'info' : 'neutral'}
                    hint={emitHint(emit) || undefined}
                />
            ))}
        </Stack>
    ) : <Typography variant="body2" color="text.secondary">—</Typography>
);

const StatusCell = ({ row, system }: { row: TagCategoryGridRow; system: string }) => {
    const status = tagCategoryStatus(row.status, system);
    const hint = crewHint(row.unlinked_crew_tags);
    return (
        <Box data-testid="tag-category-status" data-status={row.status}>
            <ToneChip label={status.label} tone={status.tone} hint={status.hint} />
            {hint && (
                <Typography variant="caption" color="warning.dark" component="div" sx={{ mt: 0.5, maxWidth: 220 }} data-testid="crew-hint">{hint}</Typography>
            )}
        </Box>
    );
};

/** "Tag categories" on a booking connection's card (backend plan 2026-10-29 sdtc WP-G): the operator-wide default
 *  tag categories against what this system writes, a row per category, mismatches first. Loaded when opened. With
 *  `editable` (`tour_operator.write`): Add (pre-filled with the declaration's seed), Edit (compare-and-set of the
 *  changed fields), Remove, and "Add all recommended" (the onboarding seed for this system, previewed first; where
 *  the grid's `seed_applicable` is false — a licensee, a sandbox with production data — its reason instead). A
 *  franchise parent's rows are read-only here. A 409 reloads the table and says the defaults changed elsewhere. */
const TagCategoriesSection = ({ connection }: { connection: Integration }) => {
    const { api } = useIntegrations();
    const { enqueueSnackbar } = useSnackbar();
    const [open, setOpen] = React.useState(false);
    const [grid, setGrid] = React.useState<TagCategoryGrid | null>(null);
    const [loadError, setLoadError] = React.useState<unknown>(null);
    const [notice, setNotice] = React.useState<string | null>(null);
    const [onlyEmitted, setOnlyEmitted] = React.useState(false);
    const [editing, setEditing] = React.useState<Editing | null>(null);
    const [removing, setRemoving] = React.useState<TagCategoryGridRow | null>(null);
    const [seeding, setSeeding] = React.useState(false);
    const generation = React.useRef(0);

    const load = React.useCallback(async () => {
        const mine = ++generation.current;
        setLoadError(null);
        try {
            const answer = await api.tagCategories(connection.id);
            if (mine === generation.current) {
                setGrid(answer);
            }
        } catch (e) {
            if (mine === generation.current) {
                setLoadError(e);
            }
        }
    }, [api, connection.id]);

    React.useEffect(() => {
        if (open) {
            load();
        }
        return () => { generation.current += 1; };
    }, [open, load]);

    /** One write: the answer is the grid after it. A 409 closes the dialog, reloads and says why; any other
     *  refusal stays in the dialog. */
    const write = async (call: () => Promise<TagCategoryGrid>, done: string) => {
        try {
            const answer = await call();
            generation.current += 1;
            setGrid(answer);
            setNotice(null);
            enqueueSnackbar(done, { variant: 'success' });
        } catch (e) {
            if (e instanceof DeveloperApiError && e.status === 409) {
                setNotice(conflictNotice(e));
                load();
                return;
            }
            throw e;
        }
    };

    const toggle = () => {
        if (open) {
            setOpen(false);
            setGrid(null);
            setNotice(null);
            setLoadError(null);
        } else {
            setOpen(true);
        }
    };

    const system = grid?.system || connection.name;
    const contentId = `tag-categories-${connection.id}`;
    const rows = grid ? sortRows(grid.rows).filter((row) => !onlyEmitted || row.emits.length > 0) : [];
    const flagLabels = grid?.flag_labels || {};
    const editable = !!grid?.editable;
    // Absent (an older backend) counts as applicable; the seed's own 409 then says why not.
    const seedApplicable = grid?.seed_applicable !== false;
    const showOwn = !!grid?.rows.some((row) => row.default && !row.default.own);
    // The effective defaults' check-in / boarding categories other than the one in the dialog (at most 3 and 1).
    const others = (flag: string, row: TagCategoryGridRow) => (grid?.rows || [])
        .filter((r) => r !== row && r.default && r.default.flags[flag]).length;

    return (
        <CardSection
            title="Tag categories"
            testId="tag-categories"
            contentId={contentId}
            actions={<Button size="small" aria-expanded={open} aria-controls={contentId} onClick={toggle}>{open ? 'Hide' : 'Show'}</Button>}
        >
            {!open && (
                <Typography variant="body2" color="text.secondary" data-testid="tag-categories-summary">
                    Which tag categories {connection.name} writes, and the default settings each one gets.
                </Typography>
            )}
            {open && loadError ? <ErrorAlert error={loadError} onRetry={load} /> : null}
            {open && !grid && !loadError && (
                <Box role="status" aria-busy="true" aria-label="Loading the tag categories" data-testid="tag-categories-loading">
                    {[0, 1, 2].map((i) => <Skeleton key={i} variant="text" height={32} />)}
                </Box>
            )}
            {open && grid && (
                <Box data-testid="tag-category-panel">
                    <Typography variant="body2" color="text.secondary" data-testid="tag-categories-header">
                        Operator-wide defaults — shared by all integrations
                    </Typography>
                    {!grid.use_operator_defaults && (
                        <Alert severity="info" sx={{ mt: 1 }} data-testid="operator-defaults-off">
                            Defaults are not applied to trips unless the trip&apos;s own switch is on
                        </Alert>
                    )}
                    {notice && (
                        <Alert severity="warning" sx={{ mt: 1 }} onClose={() => setNotice(null)} data-testid="tag-categories-notice">{notice}</Alert>
                    )}
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ my: 1, rowGap: 1 }}>
                        <FormControlLabel
                            control={<Switch size="small" checked={onlyEmitted} onChange={(ev) => setOnlyEmitted(ev.target.checked)} />}
                            label={<Typography variant="body2">Only categories {system} emits</Typography>}
                        />
                        <Box sx={{ flexGrow: 1 }} />
                        {editable && seedApplicable && <Button size="small" variant="outlined" onClick={() => setSeeding(true)}>Add all recommended</Button>}
                        {editable && !seedApplicable && (
                            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 360 }} data-testid="seed-not-applicable-hint">
                                {grid.seed_not_applicable_reason || SEED_NOT_APPLICABLE_HINT}
                            </Typography>
                        )}
                    </Stack>
                    <TableScroll>
                        <Table size="small" data-testid="tag-category-grid">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Category</TableCell>
                                    <TableCell>Default</TableCell>
                                    <TableCell>Emitted by {system}</TableCell>
                                    <TableCell>Status</TableCell>
                                    {editable && <TableCell align="right">Actions</TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row) => {
                                    const name = rowName(row);
                                    const own = !!row.default?.own;
                                    return (
                                        <TableRow key={rowKey(row)} data-testid={`tc-row-${rowKey(row)}`} sx={{ verticalAlign: 'top' }}>
                                            <TableCell><CategoryCell row={row} /></TableCell>
                                            <TableCell><DefaultCell row={row} flagLabels={flagLabels} showOwn={showOwn} /></TableCell>
                                            <TableCell><EmitsCell row={row} /></TableCell>
                                            <TableCell><StatusCell row={row} system={system} /></TableCell>
                                            {editable && (
                                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                    {row.default && own && (
                                                        <>
                                                            <Button size="small" aria-label={`Edit the default for ${name}`} onClick={() => setEditing({ mode: 'edit', row })}>Edit</Button>
                                                            <Button
                                                                size="small"
                                                                color="inherit"
                                                                sx={{ color: 'text.secondary' }}
                                                                aria-label={`Remove the default for ${name}`}
                                                                onClick={() => setRemoving(row)}
                                                            >
                                                                Remove
                                                            </Button>
                                                        </>
                                                    )}
                                                    {row.default && !own && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            Change it on {row.default.inherited_from?.name || 'the parent company'}
                                                        </Typography>
                                                    )}
                                                    {!row.default && row.category && ADDABLE.has(row.status) && (
                                                        <Button size="small" aria-label={`Add a default for ${name}`} onClick={() => setEditing({ mode: 'add', row })}>Add</Button>
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                })}
                                {rows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={editable ? 5 : 4}>
                                            <Typography variant="body2" color="text.secondary">
                                                {onlyEmitted ? `${system} writes none of these categories.` : `${system} writes no tag category, and there are no defaults.`}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableScroll>
                </Box>
            )}
            {grid && editing && editing.row.category && (
                <TagCategoryFlagsDialog
                    mode={editing.mode}
                    name={rowName(editing.row)}
                    code={editing.row.code}
                    intro={editing.mode === 'edit'
                        ? 'Operator-wide: every integration, and every trip that uses the defaults, shares these settings.'
                        : editing.row.seed
                            ? `Operator-wide: every integration shares it. Pre-filled with what ${system} recommends.`
                            : 'Operator-wide: every integration shares it. Every setting starts off.'}
                    initial={editing.mode === 'edit' && editing.row.default ? valuesFromDefault(editing.row.default) : valuesFromSeed(editing.row.seed)}
                    checkins={others('checkin', editing.row)}
                    boardings={others('boarding', editing.row)}
                    flagLabels={flagLabels}
                    onSave={(body) => {
                        const categoryId = editing.row.category!.id;
                        return editing.mode === 'add'
                            ? write(() => api.addTagCategory(connection.id, { category_id: categoryId, fields: body }), `Default for ${rowName(editing.row)} added`)
                            : write(() => api.updateTagCategory(connection.id, categoryId, { fields: body }), `Default for ${rowName(editing.row)} saved`);
                    }}
                    onClose={() => setEditing(null)}
                />
            )}
            {grid && removing && removing.category && (
                <RemoveDefaultDialog
                    name={rowName(removing)}
                    flagLabels={flagLabels}
                    onRemove={() => write(() => api.removeTagCategory(connection.id, removing.category!.id), `Default for ${rowName(removing)} removed`)}
                    onClose={() => setRemoving(null)}
                />
            )}
            {grid && seeding && (
                <SeedDialog
                    api={api}
                    integrationId={connection.id}
                    system={system}
                    flagLabels={flagLabels}
                    onApplied={(result) => {
                        setSeeding(false);
                        setNotice(null);
                        const n = result.added.length;
                        enqueueSnackbar(n ? `${n} ${n === 1 ? 'default' : 'defaults'} added` : 'Nothing was added', { variant: 'success' });
                        load();
                    }}
                    onClose={() => setSeeding(false)}
                />
            )}
        </CardSection>
    );
};

export default TagCategoriesSection;
